import { useCallback, useEffect, useMemo, useState } from 'react';
import I18n from 'I18n';
import PortalIdParser from 'PortalIdParser';
import { getFullUrlWithCurrentSubDomain } from 'hubspot-url-utils';
import { useQuery } from 'data-fetching-client';
import { StepOne } from './components/StepOne';
import StepTwo from './components/StepTwo';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { Wizard, WizardHeaderWithOverview, WizardStep } from 'foundations-components/transitional/components/Wizard';
import { Modal } from 'foundations-components/transitional/components/Modal';
import UIButton from 'UIComponents/button/UIButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILink from 'UIComponents/link/UILink';
import UILoadingOverlay from 'UIComponents/loading/UILoadingOverlay';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { updateDefaultColumns } from '../../actions/defaultColumns';
import { MAX_FORECASTS, MAX_FORECAST_NAME_LENGTH, TRACK_CANCEL_WIZARD, TRACK_CLOSE_WIZARD_AND_SKIP_GOAL_CREATION, TRACK_COMPLETE_WIZARD, TRACK_CREATE_FORECAST_TYPE, TRACK_CREATE_FORECAST_WIZARD_STEP_1 } from '../../constants/ForecastType';
import { getCreateWizardForecastTypeRedirectUrl, settingsManageUrl } from '../../utils/urls';
import { getUserHasSuperAdminScope } from '../../utils/authUtils';
import { shouldTrack } from '../../utils/trackerUtils';
import { GET_AUTH } from './api/auth';
import { createForecastType } from './api/forecastTypes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const STEP_ONE_INDEX = 0;
const STEP_TWO_INDEX = 1;
const getForecastNameErrorMessage = (forecastTypes, forecastName) => {
  if (forecastName === null) {
    return null;
  }
  if (forecastName === '') {
    return I18n.text('createForecastTypeWizard.stepOne.nameRequired');
  }
  if (forecastTypes.some(forecastType => forecastType.forecastName === forecastName)) {
    return I18n.text('createForecastTypeWizard.stepOne.nameExists');
  }
  return null;
};
const getForecastNameWarningMessage = forecastName => {
  if (forecastName && forecastName.length >= MAX_FORECAST_NAME_LENGTH) {
    return I18n.text('createForecastTypeWizard.stepOne.maxLength', {
      maxLength: MAX_FORECAST_NAME_LENGTH
    });
  }
  return null;
};
const CreateForecastTypeWizard = ({
  forecastTypes,
  dealProperties,
  onCloseWizard,
  onCancelWizard,
  onCreateForecastType,
  onTrack
}) => {
  var _window;
  const portalId = PortalIdParser.get();
  const baseAppURL = getFullUrlWithCurrentSubDomain();
  const location = (_window = window) === null || _window === void 0 ? void 0 : _window.location;
  const [stepIndex, setStepIndex] = useState(STEP_ONE_INDEX);
  const [createForecastLoading, setCreateForecastLoading] = useState(false);
  const [forecastName, setForecastName] = useState(null);
  const [selectedDateType, setSelectedDateType] = useState(null);
  const [selectedAmountType, setSelectedAmountType] = useState(null);
  const [forecastTypeId, setForecastTypeId] = useState(null);
  const [selectedDefaultColumns, setSelectedDefaultColumns] = useState(null);
  const forecastNameErrorMessage = getForecastNameErrorMessage(forecastTypes, forecastName);
  const forecastNameWarningMessage = getForecastNameWarningMessage(forecastName);
  const hasMaxForecasts = useMemo(() => forecastTypes.length >= MAX_FORECASTS, [forecastTypes]);
  useEffect(() => {
    if (shouldTrack(onTrack) && stepIndex === STEP_ONE_INDEX) {
      onTrack === null || onTrack === void 0 || onTrack(TRACK_CREATE_FORECAST_WIZARD_STEP_1);
    }
  }, [onTrack, stepIndex]);
  const {
    data: authData
  } = useQuery(GET_AUTH);
  const hasSuperAdminScope = getUserHasSuperAdminScope(authData === null || authData === void 0 ? void 0 : authData.auth);
  const disableCreateForecastType = !forecastName || !!forecastNameErrorMessage || !selectedDateType || !selectedAmountType || hasMaxForecasts || !hasSuperAdminScope;
  const showDisabledCreateForecastTypeTooltip = hasMaxForecasts || !hasSuperAdminScope;
  const skipGoalHref = useMemo(() => getCreateWizardForecastTypeRedirectUrl({
    forecastTypeId,
    portalId,
    baseAppURL,
    location
  }), [baseAppURL, forecastTypeId, location, portalId]);
  const closeWizardAndTrack = useCallback(type => {
    onCloseWizard();
    if (shouldTrack(onTrack)) {
      onTrack === null || onTrack === void 0 || onTrack(type);
    }
  }, [onCloseWizard, onTrack]);
  const handleOnReject = useCallback(() => {
    closeWizardAndTrack(TRACK_CANCEL_WIZARD);
    if (onCancelWizard) {
      onCancelWizard();
    }
  }, [closeWizardAndTrack, onCancelWizard]);
  const handleOnConfirm = useCallback(() => {
    closeWizardAndTrack(TRACK_COMPLETE_WIZARD);
  }, [closeWizardAndTrack]);
  const handleOnSkipGoal = useCallback(() => {
    closeWizardAndTrack(TRACK_CLOSE_WIZARD_AND_SKIP_GOAL_CREATION);
  }, [closeWizardAndTrack]);
  const handlePostCreateForecastType = useCallback(async newForecastTypeId => {
    setForecastTypeId(newForecastTypeId);
    onCreateForecastType(newForecastTypeId);
    if (selectedDefaultColumns !== null && selectedDefaultColumns !== void 0 && selectedDefaultColumns.length) {
      const columnIds = selectedDefaultColumns.map(column => column.value);
      await updateDefaultColumns({
        columns: columnIds,
        forecastTypeId: newForecastTypeId
      });
    }
    setStepIndex(STEP_TWO_INDEX);
  }, [onCreateForecastType, selectedDefaultColumns]);
  const handleOnStepIndexChange = useCallback(() => {
    if (stepIndex === STEP_ONE_INDEX) {
      if (!disableCreateForecastType) {
        setCreateForecastLoading(true);
        if (shouldTrack(onTrack)) {
          onTrack === null || onTrack === void 0 || onTrack(TRACK_CREATE_FORECAST_TYPE, {
            forecastName,
            dateProperty: selectedDateType,
            amountProperty: selectedAmountType,
            duplicateOfDefault: selectedDateType === 'closedate' && selectedAmountType === 'amount'
          });
        }
        createForecastType({
          forecastName,
          dateProperty: selectedDateType,
          amountProperty: selectedAmountType
        }).then(async ({
          forecastTypeId: newForecastTypeId,
          error
        }) => {
          if (error) {
            setStepIndex(STEP_ONE_INDEX);
          } else {
            await handlePostCreateForecastType(newForecastTypeId);
          }
        }).catch(() => {
          // do nothing, errors caught in the api function
        }).finally(() => {
          setCreateForecastLoading(false);
        });
      }
    }
  }, [disableCreateForecastType, forecastName, handlePostCreateForecastType, onTrack, selectedAmountType, selectedDateType, stepIndex]);
  return /*#__PURE__*/_jsxs(Modal, {
    use: "fullscreen",
    children: [createForecastLoading && /*#__PURE__*/_jsx(UILoadingOverlay, {
      showLabel: true,
      label: I18n.text('createForecastTypeWizard.loading'),
      layout: "centered",
      "data-test-id": "create-forecast-loading",
      "data-selenium-test": "create-forecast-loading"
    }), /*#__PURE__*/_jsxs(Wizard, {
      backable: false,
      cancellable: stepIndex === STEP_ONE_INDEX,
      defaultHeading: I18n.text('createForecastTypeWizard.title'),
      defaultNextLabel: I18n.text('createForecastTypeWizard.finish'),
      defaultDoneButton: /*#__PURE__*/_jsx(UIFlex, {
        direction: "row",
        justify: "end",
        align: "center",
        children: /*#__PURE__*/_jsx(UIButton, {
          use: "primary",
          href: skipGoalHref,
          onClick: handleOnSkipGoal,
          children: /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            gap: "200",
            children: [/*#__PURE__*/_jsx(Text, {
              as: "p",
              typography: "heading-50",
              color: "text-primary-on-fill-default",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "createForecastTypeWizard.stepTwo.goToForecast"
              })
            }), /*#__PURE__*/_jsx(UIIcon, {
              name: "right"
            })]
          })
        })
      }),
      disablePrimaryButton: disableCreateForecastType,
      primaryButtonTooltip: /*#__PURE__*/_jsxs(_Fragment, {
        children: [!hasSuperAdminScope && /*#__PURE__*/_jsx(FormattedMessage, {
          message: "createForecastTypeWizard.createForecastNotAdminTooltip"
        }), hasSuperAdminScope && hasMaxForecasts && /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "createForecastTypeWizard.createForecastMaxTooltip_jsx",
          options: {
            href: settingsManageUrl(portalId),
            maxforecasts: MAX_FORECASTS
          },
          elements: {
            Link: UILink
          }
        })]
      }),
      primaryButtonTooltipProps: {
        disabled: !showDisabledCreateForecastTypeTooltip || stepIndex === STEP_TWO_INDEX
      },
      stepIndex: stepIndex,
      headerComponent: WizardHeaderWithOverview,
      onReject: handleOnReject,
      onConfirm: handleOnConfirm,
      onStepIndexChange: handleOnStepIndexChange,
      stickyHeader: false,
      children: [/*#__PURE__*/_jsx(WizardStep, {
        children: /*#__PURE__*/_jsx(Flex, {
          justifyContent: "center",
          children: /*#__PURE__*/_jsx(StepOne, {
            dealProperties: dealProperties,
            forecastName: forecastName,
            forecastNameErrorMessage: forecastNameErrorMessage,
            forecastNameWarningMessage: forecastNameWarningMessage,
            onTrack: onTrack,
            selectedDefaultColumns: selectedDefaultColumns,
            selectedAmountType: selectedAmountType,
            setForecastName: setForecastName,
            setSelectedAmountType: setSelectedAmountType,
            setSelectedDateType: setSelectedDateType,
            setSelectedDefaultColumns: setSelectedDefaultColumns
          })
        })
      }), /*#__PURE__*/_jsx(WizardStep, {
        children: /*#__PURE__*/_jsx(Flex, {
          justifyContent: "center",
          children: /*#__PURE__*/_jsx(StepTwo, {
            closeWizardAndTrack: closeWizardAndTrack,
            forecastTypeId: forecastTypeId
          })
        })
      })]
    })]
  });
};
export default CreateForecastTypeWizard;